import { useState, useEffect, useRef } from "react";
import {
  Bot, Shield, DollarSign, Brain, Zap,
  ToggleLeft, ToggleRight, AlertTriangle,
  Trash2, Download, Ban, AlertCircle, Eye,
  CheckCircle2, RotateCcw, Plus, ChevronDown,
  TestTube2, Loader2, Check, ExternalLink, KeyRound,
  EyeOff, LogOut, Plug, Coins, Info, X, GripVertical,
} from "lucide-react";
import { useAI } from "../../context/AIContext";
import toast from "react-hot-toast";
import { ModelsTab, ApiKeysTab, modelsTabCss } from "./ModelsTab";

// ─── Design tokens ────────────────────────────────────────────────────────────
const blue    = "var(--lyra-color-primary)";
const blueBg  = "var(--app-primary-soft)";
const bord    = "var(--lyra-color-border)";
const tx      = "var(--lyra-color-text)";
const mu      = "var(--app-text-3)";
const su      = "var(--lyra-color-text-muted)";
const shadow  = "0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)";

// ─── Model metadata ───────────────────────────────────────────────────────────
const MODEL_META = {
  gemini:         { freeTokens: 10000,  docsUrl: "https://aistudio.google.com/app/apikey",            docsLabel: "Google AI Studio" },
  groq:           { freeTokens: 20000,  docsUrl: "https://console.groq.com/keys",                     docsLabel: "Groq Console" },
  "gemma-4-31b":  { freeTokens: 999999, docsUrl: "https://openrouter.ai/keys",                        docsLabel: "OpenRouter" },
  "mistral-7b":   { freeTokens: 0,      docsUrl: "https://openrouter.ai/keys",                        docsLabel: "OpenRouter" },
  "deepseek-r1":  { freeTokens: 999999, docsUrl: "https://openrouter.ai/keys",                        docsLabel: "OpenRouter" },
  openrouter:     { freeTokens: 999999, docsUrl: "https://openrouter.ai/keys",                        docsLabel: "OpenRouter" },
  kimi:           { freeTokens: 10000,  docsUrl: "https://platform.moonshot.cn/console/api-keys",     docsLabel: "Moonshot Console" },
  openai:         { freeTokens: 0,      docsUrl: "https://platform.openai.com/api-keys",              docsLabel: "OpenAI Platform" },
  ollama:         { freeTokens: 999999, docsUrl: "https://ollama.com/download",                       docsLabel: "Ollama Docs" },
  together:       { freeTokens: 10000,  docsUrl: "https://api.together.ai/settings/api-keys",         docsLabel: "Together AI" },
  "claude-opus-4-5":   { freeTokens: 0, docsUrl: "https://console.anthropic.com/settings/keys",      docsLabel: "Anthropic" },
  "claude-sonnet-4-5": { freeTokens: 0, docsUrl: "https://console.anthropic.com/settings/keys",      docsLabel: "Anthropic" },
  "claude-haiku-3-5":  { freeTokens: 0, docsUrl: "https://console.anthropic.com/settings/keys",      docsLabel: "Anthropic" },
};

const PROVIDER_COLORS = {
  google:     { bg: 'var(--lyra-color-surface-2)', text: '#4285F4' },
  anthropic:  { bg: 'var(--lyra-color-surface-2)', text: '#D97757' },
  openai:     { bg: 'var(--lyra-color-surface-2)', text: 'var(--lyra-color-text)' },
  groq:       { bg: 'var(--lyra-color-surface-2)', text: '#F55036' },
  openrouter: { bg: 'var(--app-surface-3)', text: '#6366F1' },
  moonshot:   { bg: 'var(--app-primary-soft)', text: '#0EA5E9' },
  together:   { bg: 'var(--lyra-color-surface-2)', text: 'var(--lyra-color-warning)' },
  ollama:     { bg: 'var(--lyra-color-surface-2)', text: '#444444' },
};

const PROVIDER_ICONS = {
  // Google — official 4-colour G logo
  google: (
    <svg width="15" height="15" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  ),

  // Anthropic — correct two-triangle "A" mark (no crossbar, wide base)
  anthropic: (
    <svg width="15" height="15" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path fill="#D97757" d="M17.33 3.52h-3.28L24 20.48h3.28L17.33 3.52z M6.67 3.52L0 20.48h3.28l1.38-3.56h6.81l1.38 3.56h3.28L10.03 3.52H6.67zm-.6 10.96l2.27-5.88 2.27 5.88H6.07z"/>
    </svg>
  ),

  // OpenAI — official bloom/rosette mark
  openai: (
    <svg width="15" height="15" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path fill="#000" d="M22.28 9.82a5.98 5.98 0 00-.52-4.91 6.05 6.05 0 00-6.51-2.9A6.07 6.07 0 009.98.84a6.05 6.05 0 00-5.77 4.2 5.98 5.98 0 00-3.99 2.9 6.05 6.05 0 00.74 7.1 5.98 5.98 0 00.51 4.91 6.05 6.05 0 006.51 2.9A5.98 5.98 0 0013.26 24a6.06 6.06 0 005.77-4.21 5.99 5.99 0 003.99-2.9 6.06 6.06 0 00-.74-7.07zm-9.02 12.61a4.48 4.48 0 01-2.88-1.04l.14-.08 4.78-2.76a.79.79 0 00.39-.68v-6.74l2.02 1.17a.07.07 0 01.04.05v5.58a4.5 4.5 0 01-4.49 4.5zm-9.66-4.13a4.47 4.47 0 01-.53-3.01l.14.08 4.78 2.76a.77.77 0 00.78 0l5.84-3.37v2.33l-.03.06-4.84 2.79a4.5 4.5 0 01-6.14-1.64zM2.34 7.9a4.49 4.49 0 012.37-1.97V11.6a.77.77 0 00.39.68l5.81 3.35-2.02 1.17-.07-.01-4.83-2.79A4.5 4.5 0 012.34 7.9zm16.6 3.86l-5.84-3.37 2.02-1.17.07.01 4.83 2.79a4.49 4.49 0 01-.68 8.1v-5.67a.79.79 0 00-.4-.69zm2.01-3.02l-.14-.09-4.77-2.78a.78.78 0 00-.79 0L9.41 9.23V6.9l.03-.06 4.83-2.79a4.5 4.5 0 016.68 4.69zM8.31 12.86l-2.02-1.16-.04-.06V6.07a4.5 4.5 0 017.38-3.45l-.14.08-4.79 2.76a.79.79 0 00-.39.68zm1.1-2.37l2.6-1.5 2.6 1.5v2.99l-2.6 1.5-2.6-1.5z"/>
    </svg>
  ),

  // Groq — official wordmark "G" letterform
  groq: (
    <svg width="15" height="15" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <path fill="#F55036" d="M50 5C25.15 5 5 25.15 5 50s20.15 45 45 45 45-20.15 45-45S74.85 5 50 5zm0 10c19.33 0 35 15.67 35 35 0 5.97-1.49 11.6-4.12 16.5L30.5 21.62A34.8 34.8 0 0150 15zm0 70c-19.33 0-35-15.67-35-35 0-5.97 1.49-11.6 4.12-16.5l50.38 43.88A34.8 34.8 0 0150 85z"/>
    </svg>
  ),

  // OpenRouter — their actual stylised "OR" chevron arrows mark
  openrouter: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16.5 3l4.5 4.5-4.5 4.5" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3 7.5h18" stroke="#6366F1" strokeWidth="2" strokeLinecap="round"/>
      <path d="M7.5 21l-4.5-4.5L7.5 12" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M21 16.5H3" stroke="#6366F1" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  // Moonshot / Kimi — their "K" letterform in brand blue
  moonshot: (
    <svg width="15" height="15" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path fill="#0EA5E9" d="M6 3h3v7.5l6-7.5h3.8L12.5 11 19 21h-3.8l-4.7-7.2-2 2.4V21H6V3z"/>
    </svg>
  ),

  // Together AI — their actual "T" mark with dot
  together: (
    <svg width="15" height="15" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path fill="var(--lyra-color-warning)" d="M3 4h18v3H14v13h-4V7H3V4z"/>
      <circle cx="19" cy="19" r="2.5" fill="var(--lyra-color-warning)"/>
    </svg>
  ),

  // Ollama — their official llama head mark
  ollama: (
    <svg width="15" height="15" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <path fill="var(--app-text-2)" d="M30 70 C30 70 20 68 18 58 C16 48 22 40 30 38 C30 30 34 20 50 20 C66 20 70 30 70 38 C78 40 84 48 82 58 C80 68 70 70 70 70 L30 70z"/>
      <circle cx="38" cy="44" r="4" fill="var(--app-on-accent)"/>
      <circle cx="62" cy="44" r="4" fill="var(--app-on-accent)"/>
      <circle cx="39" cy="44" r="2" fill="#222"/>
      <circle cx="63" cy="44" r="2" fill="#222"/>
      <path fill="var(--app-text-2)" d="M38 72 C38 72 36 80 30 82 L34 84 C34 84 40 80 42 72 L38 72z M62 72 C62 72 64 80 70 82 L66 84 C66 84 60 80 58 72 L62 72z"/>
    </svg>
  ),
};

// PROVIDER_CATALOG removed — catalog now served live from GET /api/models/catalog
// (data lives in server/src/routes/modelCatalogRoutes.js → STATIC_MODELS_BY_PROVIDER)

// ─── Shared atoms ─────────────────────────────────────────────────────────────
function Toggle({ on, onToggle }) {
  return (
    <button onClick={onToggle} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", flexShrink: 0 }}>
      {on ? <ToggleRight size={26} color={blue} /> : <ToggleLeft size={26} color={mu} />}
    </button>
  );
}

function Pill({ children, active, onClick }) {
  return (
    <button onClick={onClick} style={{ padding: "5px 14px", borderRadius: 8, fontSize: 12.5, fontWeight: 600, border: "1.5px solid " + (active ? blue : bord), background: active ? blueBg : "var(--lyra-color-surface)", color: active ? blue : su, cursor: "pointer", transition: "all 0.15s" }}>
      {children}
    </button>
  );
}

function SectionTitle({ children, style }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: mu, textTransform: "uppercase", letterSpacing: "0.07em", margin: "22px 0 10px", ...style }}>
      {children}
    </div>
  );
}

function StatCard({ value, label, sub, color }) {
  return (
    <div style={{ background: "var(--lyra-color-surface-2)", border: "1px solid " + bord, borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || tx }}>{value}</div>
      <div style={{ fontSize: 11, color: mu, marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: blue, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function SliderField({ label, value, onChange, min = 0, max = 100, unit = "" }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: tx }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: blue }}>{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} value={value} onChange={e => onChange(+e.target.value)} style={{ width: "100%", accentColor: blue, cursor: "pointer" }} />
    </div>
  );
}

function Badge({ label, color, bg }) {
  return <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: bg, color }}>{label}</span>;
}

const LEGEND_ITEMS = [
  ["var(--lyra-color-success)", "Active / free tier available"],
  ["var(--lyra-color-warning)", "Near limit or key required"],
  ["var(--lyra-color-danger)", "Exhausted \u2014 add key"],
  ["var(--app-border-strong)", "Disabled"],
];

function LegendTooltip() {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <Info size={13} color="var(--app-text-3)" style={{ cursor: "help", display: "block" }} />
      {show && (
        <div style={{ position: "absolute", top: "calc(100% + 7px)", left: "50%", transform: "translateX(-50%)", background: "var(--lyra-color-text)", borderRadius: 9, padding: "10px 13px", zIndex: 200, boxShadow: "0 6px 20px rgba(0,0,0,0.18)", pointerEvents: "none" }}>
          <div style={{ position: "absolute", top: -5, left: "50%", transform: "translateX(-50%)", width: 10, height: 10, background: "var(--lyra-color-text)", clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)" }} />
          {LEGEND_ITEMS.map(([c, l]) => (
            <div key={c} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5, whiteSpace: "nowrap" }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: c, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: "var(--lyra-color-border)" }}>{l}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Btn({ children, onClick, danger, primary, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 13px", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", border: "none", background: primary ? blue : danger ? "var(--lyra-color-danger-bg)" : "var(--lyra-color-surface-2)", color: primary ? "var(--lyra-color-surface)" : danger ? "var(--lyra-color-danger)" : su, opacity: disabled ? 0.5 : 1 }}>
      {children}
    </button>
  );
}

function SettingRow({ icon, label, hint, children, last }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderBottom: last ? "none" : "1px solid " + bord }}>
      {icon && <div style={{ flexShrink: 0, color: mu }}>{icon}</div>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: tx }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: mu, marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}

// ─── Mini token bar ───────────────────────────────────────────────────────────
function MiniTokenBar({ tokensUsed = 0, freeTokens = 0 }) {
  const unlimited = freeTokens >= 999999;
  const noFree    = freeTokens === 0;
  const pct       = unlimited || noFree ? 0 : Math.min(100, Math.round((tokensUsed / freeTokens) * 100));
  const exhausted = !unlimited && !noFree && tokensUsed >= freeTokens;
  const nearLimit = !unlimited && !noFree && pct >= 80 && !exhausted;
  const barColor  = exhausted ? "var(--lyra-color-danger)" : nearLimit ? "var(--lyra-color-warning)" : "var(--lyra-color-success)";
  const fmtNum    = n => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

  if (noFree) return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
      <span style={{ fontSize: 11, color: "var(--lyra-color-warning)", fontWeight: 600 }}>⚠ Key required</span>
    </div>
  );
  if (unlimited) return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
      <div style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--lyra-color-success)" }} />
      <span style={{ fontSize: 11, color: su, fontWeight: 600 }}>Unlimited</span>
    </div>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
      <div style={{ width: 60, height: 4, borderRadius: 99, background: "var(--lyra-color-border)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: pct + "%", background: barColor, borderRadius: 99, transition: "width 0.4s" }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: exhausted ? "var(--lyra-color-danger)" : nearLimit ? "var(--lyra-color-warning)" : su, whiteSpace: "nowrap" }}>
        {fmtNum(tokensUsed)}/{fmtNum(freeTokens)}
      </span>
    </div>
  );
}

// ─── Single model row ─────────────────────────────────────────────────────────
function ModelRow({ model, isSelected, selectedMode, onSelect, customApiKey = "", ollamaModelKey = "", onSetKey, onToggle, enabled }) {
  const isOllama    = model.id === "ollama";

  const [expanded,   setExpanded]   = useState(false);
  const [showKey,    setShowKey]    = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [ollamaModels,     setOllamaModels]     = useState([]);
  const [loadingModels,    setLoadingModels]    = useState(false);

  // Local draft — the key only actually saves (and gets live-verified
  // server-side) on blur, not on every keystroke. Previously this saved on
  // every keystroke with no verification at all, so a bad/typo'd key would
  // silently "save" and sit there looking configured.
  const [keyDraft, setKeyDraft] = useState(customApiKey);
  const [savingKey, setSavingKey] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setKeyDraft(customApiKey); }, [model.id]);

  async function handleKeySave() {
    if (keyDraft === customApiKey) return;
    setSavingKey(true);
    setTestResult(null);
    try {
      await onSetKey(model.id, keyDraft);
      if (keyDraft) toast.success(`${model.name} key saved`);
    } catch (err) {
      const detail = err?.response?.data?.details?.[model.id] || err?.response?.data?.error || err.message;
      toast.error(detail || "Failed to save key");
      setKeyDraft(customApiKey); // revert — the bad value was never persisted
    } finally {
      setSavingKey(false);
    }
  }

  // Load available Ollama models when the row is expanded or the URL changes
  useEffect(() => {
    if (!isOllama || !expanded) return;
    setLoadingModels(true);
    const url = customApiKey && customApiKey.startsWith("http") ? customApiKey : "http://localhost:11434";
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");
    fetch(`${process.env.REACT_APP_API_URL || ""}/api/usage/ollama-models?url=${encodeURIComponent(url)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.ok) setOllamaModels(d.models || []); })
      .catch(() => {})
      .finally(() => setLoadingModels(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOllama, expanded, customApiKey]);

  const meta        = MODEL_META[model.id] || {};
  const freeTokens  = meta.freeTokens ?? 0;
  const tokensUsed  = model.tokensUsed ?? 0;
  const unlimited   = freeTokens >= 999999;
  const noFree      = freeTokens === 0;
  const exhausted   = !unlimited && !noFree && tokensUsed >= freeTokens;
  const keyPh       = isOllama ? "http://your-ollama-host:11434" : `${model.name} API key…`;

  const dotColor = !enabled ? "var(--app-border-strong)"
    : (noFree && !customApiKey)     ? "var(--lyra-color-warning)"
    : (exhausted && !customApiKey)  ? "var(--lyra-color-danger)"
    : "var(--lyra-color-success)";

  async function handleTest() {
    if (!keyDraft) return;
    setTesting(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const res   = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/usage/test-key`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ modelId: model.id, apiKey: keyDraft }),
      });
      const data = await res.json();
      setTestResult(data);
      if (data.ok) toast.success(`${model.name} connected · ${data.latencyMs}ms`);
      else toast.error(data.error || "Test failed");
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
      toast.error(err.message);
    } finally {
      setTesting(false);
    }
  }

  const getBadgeStyle = badge => {
    if (badge === "FREE")        return { color: "var(--lyra-color-success)", bg: "var(--lyra-color-success-bg)" };
    if (badge === "SELF-HOSTED") return { color: "var(--lyra-color-text-muted)", bg: "var(--lyra-color-surface-2)" };
    return { color: "var(--lyra-color-primary)", bg: "var(--app-primary-soft)" };
  };
  const { color: badgeColor, bg: badgeBg } = getBadgeStyle(model.badge);

  return (
    <div style={{ border: "1.5px solid " + (isSelected && !expanded ? "var(--app-primary-soft-2)" : bord), borderRadius: 10, overflow: "hidden", transition: "border-color 0.15s", opacity: enabled ? 1 : 0.5 }}>

      {/* Collapsed row */}
      <div
        onClick={() => {
          setExpanded(v => !v);
          if (selectedMode === "manual" && enabled) onSelect(model.id);
        }}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: expanded ? "var(--lyra-color-surface-2)" : "var(--lyra-color-surface)", cursor: "pointer", userSelect: "none" }}
        onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = "var(--lyra-color-surface-2)"; }}
        onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = expanded ? "var(--lyra-color-surface-2)" : "var(--lyra-color-surface)"; }}
      >
        <div style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: dotColor }} />

        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 7, overflow: "hidden" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: tx, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {model.name}
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: badgeBg, color: badgeColor, whiteSpace: "nowrap", flexShrink: 0 }}>
            {model.badge}
          </span>
          {isSelected && selectedMode === "manual" && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: blueBg, color: blue, flexShrink: 0 }}>ACTIVE</span>
          )}
          {!enabled && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: "var(--lyra-color-warning-bg)", color: "var(--lyra-color-warning)", flexShrink: 0 }}>SKIPPED</span>
          )}
        </div>

        <MiniTokenBar tokensUsed={tokensUsed} freeTokens={freeTokens} />

        <div onClick={e => e.stopPropagation()}>
          <button
            onClick={() => onToggle(model.id)}
            style={{ position: "relative", width: 34, height: 18, borderRadius: 999, border: "none", cursor: "pointer", padding: 0, outline: "none", background: enabled ? "var(--lyra-color-success)" : "var(--app-border-strong)", transition: "background 0.2s", flexShrink: 0 }}
          >
            <span style={{ position: "absolute", top: 2, left: enabled ? 17 : 2, width: 14, height: 14, borderRadius: "50%", background: "var(--lyra-color-surface)", boxShadow: "0 1px 3px rgba(0,0,0,0.25)", transition: "left 0.2s", display: "block" }} />
          </button>
        </div>

        <div style={{ color: mu, flexShrink: 0, transition: "transform 0.2s", transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}>
          <ChevronDown size={14} />
        </div>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div style={{ borderTop: "1px solid " + bord, background: "var(--lyra-color-surface-2)", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 12, color: su }}>{model.description}</div>

          {!noFree && (
            <div style={{ background: "var(--lyra-color-surface)", border: "1px solid " + bord, borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <Coins size={12} color={mu} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: su }}>
                    {unlimited ? "Unlimited free tier" : exhausted ? "Free tier exhausted this month" : "Free tokens this month"}
                  </span>
                </div>
                {!unlimited && (
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: exhausted ? "var(--lyra-color-danger)" : su }}>
                    {tokensUsed.toLocaleString()} / {freeTokens.toLocaleString()}
                  </span>
                )}
              </div>
              {!unlimited && (
                <>
                  <div style={{ height: 6, borderRadius: 99, background: "var(--lyra-color-border)", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: Math.min(100, (tokensUsed / freeTokens) * 100) + "%", background: exhausted ? "var(--lyra-color-danger)" : tokensUsed / freeTokens >= 0.8 ? "var(--lyra-color-warning)" : "var(--lyra-color-success)", borderRadius: 99, transition: "width 0.6s" }} />
                  </div>
                  {exhausted && (
                    <div style={{ fontSize: 11, color: "var(--lyra-color-danger)", marginTop: 5, fontWeight: 600 }}>
                      ⚠ Add your own API key to keep using this model. Resets next month.
                    </div>
                  )}
                </>
              )}
              {unlimited && (
                <div style={{ fontSize: 11.5, color: su }}>
                  {isOllama ? "Runs on your own hardware — no API costs." : "OpenRouter hosts this model for free. No cost to you or Lyra."}
                </div>
              )}
            </div>
          )}

          {noFree && (
            <div style={{ background: "var(--app-primary-soft)", border: "1px solid var(--app-primary-soft-2)", borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "var(--app-primary-deep)" }}>
              ⚠ This model has no free tier. You must provide your own API key.
            </div>
          )}

          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: su, marginBottom: 6 }}>
              {noFree || exhausted ? "API Key (required)" : "API Key (optional — use your own quota)"}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1, position: "relative" }}>
                <input
                  type={showKey ? "text" : "password"}
                  placeholder={keyPh}
                  value={keyDraft}
                  disabled={savingKey}
                  onChange={e => { setKeyDraft(e.target.value); setTestResult(null); }}
                  style={{ width: "100%", padding: "8px 36px 8px 12px", border: "1.5px solid " + (testResult?.ok ? "var(--app-success-border)" : testResult?.ok === false ? "var(--app-danger-border)" : bord), borderRadius: 9, fontSize: 12.5, color: tx, outline: "none", background: savingKey ? "var(--lyra-color-surface-2)" : "var(--lyra-color-surface)", fontFamily: "inherit", boxSizing: "border-box", transition: "border-color 0.15s" }}
                  onFocus={e => e.target.style.borderColor = blue}
                  onBlur={e => { e.target.style.borderColor = (testResult?.ok ? "var(--app-success-border)" : testResult?.ok === false ? "var(--app-danger-border)" : bord); handleKeySave(); }}
                />
                <button type="button" onClick={() => setShowKey(v => !v)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: mu, padding: 2 }}>
                  {showKey ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>

              <button
                onClick={handleTest}
                disabled={!keyDraft || testing}
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 13px", borderRadius: 9, border: "1.5px solid " + (testResult?.ok ? "var(--app-success-border)" : testResult?.ok === false ? "var(--app-danger-border)" : bord), background: testResult?.ok ? "var(--lyra-color-success-bg)" : testResult?.ok === false ? "var(--lyra-color-danger-bg)" : "var(--lyra-color-surface)", color: testResult?.ok ? "var(--lyra-color-success)" : testResult?.ok === false ? "var(--lyra-color-danger)" : su, cursor: keyDraft && !testing ? "pointer" : "not-allowed", opacity: keyDraft ? 1 : 0.45, fontWeight: 600, fontSize: 12.5, flexShrink: 0, transition: "all 0.15s" }}
              >
                {testing
                  ? <><Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> Testing</>
                  : testResult?.ok
                    ? <><Check size={13} /> {testResult.latencyMs}ms</>
                    : <><TestTube2 size={13} /> Test</>}
              </button>

              {meta.docsUrl && (
                <a href={meta.docsUrl} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 4, padding: "8px 12px", borderRadius: 9, border: "1.5px solid " + bord, background: "var(--lyra-color-surface)", color: su, textDecoration: "none", fontSize: 12.5, fontWeight: 600, flexShrink: 0, transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--lyra-color-surface-2)"}
                  onMouseLeave={e => e.currentTarget.style.background = "var(--lyra-color-surface)"}>
                  <KeyRound size={12} /> {meta.docsLabel} <ExternalLink size={11} />
                </a>
              )}
            </div>

            {savingKey && (
              <div style={{ fontSize: 11.5, color: mu, marginTop: 5, fontWeight: 500, display: "flex", alignItems: "center", gap: 5 }}>
                <Loader2 size={11} style={{ animation: "spin 0.7s linear infinite" }} /> Verifying &amp; saving…
              </div>
            )}
            {testResult && !testResult.ok && (
              <div style={{ fontSize: 11.5, color: "var(--lyra-color-danger)", marginTop: 5, fontWeight: 500 }}>✗ {testResult.error}</div>
            )}
            {testResult?.ok && (
              <div style={{ fontSize: 11.5, color: "var(--lyra-color-success)", marginTop: 5, fontWeight: 600 }}>✓ Connected successfully · {testResult.latencyMs}ms latency</div>
            )}
          </div>

          {/* Ollama model picker */}
          {isOllama && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: su, marginBottom: 6 }}>
                Model {loadingModels && <span style={{ fontSize: 11, color: mu, fontWeight: 400 }}>(loading…)</span>}
              </div>
              {ollamaModels.length > 0 ? (
                <select
                  value={ollamaModelKey || ""}
                  onChange={e => onSetKey("ollama_model", e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", border: "1.5px solid " + bord, borderRadius: 9, fontSize: 12.5, color: tx, background: "var(--lyra-color-surface)", outline: "none", fontFamily: "inherit", cursor: "pointer" }}
                >
                  <option value="">— use default (llama3.2) —</option>
                  {ollamaModels.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder="e.g. llama3.2, mistral:7b, gemma3:12b"
                  value={ollamaModelKey || ""}
                  onChange={e => onSetKey("ollama_model", e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", border: "1.5px solid " + bord, borderRadius: 9, fontSize: 12.5, color: tx, outline: "none", background: "var(--lyra-color-surface)", fontFamily: "inherit", boxSizing: "border-box" }}
                />
              )}
              <div style={{ fontSize: 11, color: mu, marginTop: 4 }}>
                {ollamaModels.length > 0
                  ? `${ollamaModels.length} model${ollamaModels.length > 1 ? "s" : ""} found on your Ollama instance`
                  : "Enter a model name or start Ollama and save the URL above to auto-detect models"}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Engram API helpers ───────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL ?? "";
function authHeaders() {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

// ─── Add Model Modal ─────────────────────────────────────────────────────────
// Catalog is fetched live from GET /api/models/catalog — no hardcoded list.
function AddModelModal({ onClose, onSave, onUserModelSaved, existingKeys = {} }) {
  const [catalog,        setCatalog]        = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError,   setCatalogError]   = useState(null);
  const [selProv,        setSelProv]        = useState(null);
  const [selModel,       setSelModel]       = useState(null);
  const [isCustom,       setIsCustom]       = useState(false);
  const [customForm,     setCustomForm]     = useState({
    providerName: "", modelName: "", systemId: "", contextK: "", desc: "", keyUrl: "", baseUrl: "",
  });
  const [apiKey,     setApiKey]     = useState("");
  const [showKey,    setShowKey]    = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving,     setSaving]     = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");
    fetch(`${API_BASE}/api/models/catalog`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (d.success && d.catalog?.length) {
          setCatalog(d.catalog);
          const first = d.catalog[0];
          setSelProv(first);
          setSelModel(first.models[0] || null);
          if (first.models[0]) setApiKey(existingKeys[first.models[0].systemId] || "");
        } else {
          setCatalogError("Could not load model catalog.");
        }
      })
      .catch(() => setCatalogError("Network error loading catalog."))
      .finally(() => setCatalogLoading(false));
  }, []); // eslint-disable-line

  function autoSystemId(prov, model) {
    return `custom-${[prov, model].join("-").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
  }

  function pickProv(p) {
    setIsCustom(false);
    setSelProv(p);
    const first = p.models[0] || null;
    setSelModel(first);
    setApiKey(first ? (existingKeys[first.systemId] || "") : "");
    setTestResult(null);
  }
  function pickModel(m) {
    setSelModel(m);
    setApiKey(existingKeys[m.systemId] || "");
    setTestResult(null);
  }
  function pickCustom() {
    setIsCustom(true); setSelProv(null); setSelModel(null); setApiKey(""); setTestResult(null);
  }
  function setCustomField(field, val) {
    setCustomForm(prev => {
      const next = { ...prev, [field]: val };
      if ((field === "providerName" || field === "modelName") && !prev._sysEdited) {
        next.systemId = autoSystemId(next.providerName, next.modelName);
      }
      if (field === "systemId") next._sysEdited = true;
      return next;
    });
    setTestResult(null);
  }

  async function handleTest() {
    const targetId = isCustom ? customForm.systemId : selModel?.systemId;
    if (!apiKey.trim() || !targetId) return;
    setTesting(true); setTestResult(null);
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/usage/test-key`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ modelId: targetId, apiKey: apiKey.trim() }),
      });
      const data = await res.json();
      setTestResult(data);
      if (data.ok) toast.success(`Connected · ${data.latencyMs}ms`);
      else toast.error(data.error || "Test failed");
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    } finally { setTesting(false); }
  }

  async function handleSave() {
    // Prevent saving unless the API key was tested successfully
    if (!(testResult && testResult.ok)) {
      toast.error('Please test the API key successfully before saving.');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      if (isCustom) {
        const { providerName, modelName, systemId, contextK, desc, keyUrl, baseUrl } = customForm;
        if (!providerName.trim() || !modelName.trim() || !systemId.trim() || !apiKey.trim()) {
          toast.error("Fill in Provider, Model Name, System ID, and API Key");
          setSaving(false); return;
        }
        await fetch(`${API_BASE}/api/models/user-models`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            providerId: "custom", providerName: providerName.trim(), modelName: modelName.trim(),
            systemId: systemId.trim(), badge: "KEY", desc: desc.trim(),
            contextK: contextK ? parseInt(contextK) : null, keyUrl: keyUrl.trim(), baseUrl: baseUrl.trim() || null,
          }),
        });
        await onSave(systemId.trim(), apiKey.trim());
        onUserModelSaved && onUserModelSaved();
      } else {
        if (!selModel || !apiKey.trim()) { setSaving(false); return; }
        if (!selModel.isUserAdded && selProv) {
          await fetch(`${API_BASE}/api/models/user-models`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              providerId: selProv.id, providerName: selProv.name, modelName: selModel.name,
              systemId: selModel.systemId, badge: selModel.badge, desc: selModel.desc || "",
              contextK: selModel.contextK || null, keyUrl: selProv.keyUrl || "", keyLabel: selProv.keyLabel || "",
            }),
          });
          onUserModelSaved && onUserModelSaved();
        }
        await onSave(selModel.systemId, apiKey.trim());
      }
      onClose();
    } catch (err) {
      const modelIdForError = isCustom ? customForm.systemId?.trim() : selModel?.systemId;
      const detail = err?.response?.data?.details?.[modelIdForError] || err?.response?.data?.error || err.message;
      toast.error(detail || "Failed to save");
    } finally { setSaving(false); }
  }

  const BADGE_S = { FREE: { color: "var(--lyra-color-success)", bg: "var(--lyra-color-success-bg)" }, KEY: { color: "var(--lyra-color-warning)", bg: "var(--lyra-color-warning-bg)" }, PREMIUM: { color: "var(--lyra-color-primary)", bg: "var(--app-primary-soft)" } };
  const canSave = apiKey.trim() && (isCustom
    ? customForm.providerName.trim() && customForm.modelName.trim() && customForm.systemId.trim()
    : !!selModel);

  return (
    <>
      <style>{`
        @keyframes amIn { from { opacity:0; transform:translateY(22px) scale(0.97) } to { opacity:1; transform:translateY(0) scale(1) } }
        @keyframes amBg { from { opacity:0 } to { opacity:1 } }
      `}</style>
      <div
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.38)", backdropFilter:"blur(3px)", zIndex:8500, display:"flex", alignItems:"center", justifyContent:"center", padding:"16px", animation:"amBg 0.18s ease" }}
      >
        <div
          onClick={e => e.stopPropagation()}
          className="add-model-shell"
        >
          {/* Header */}
          <div style={{ display:"flex", alignItems:"center", gap:12, padding:"18px 20px 16px", borderBottom:"1px solid "+bord, flexShrink:0 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:blueBg, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <KeyRound size={17} color={blue} />
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:15, fontWeight:700, color:tx }}>Add AI Model</div>
              <div style={{ fontSize:12, color:mu }}>Choose a provider, select a model, and add your API key</div>
            </div>
            <button onClick={onClose} style={{ width:30, height:30, borderRadius:8, border:"1px solid "+bord, background:"var(--lyra-color-surface-2)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:su }}>
              <X size={14} />
            </button>
          </div>

          <div className="add-model-content">
            {/* Left — provider sidebar */}
            <div className="add-model-provider-col">
              <div style={{ fontSize:10, fontWeight:700, color:mu, textTransform:"uppercase", letterSpacing:"0.07em", padding:"2px 8px 8px" }}>Provider</div>

              {catalogLoading ? (
                <div style={{ display:"flex", alignItems:"center", justifyContent:"center", flex:1, gap:8, color:mu, fontSize:12 }}>
                  <Loader2 size={14} style={{ animation:"spin 0.8s linear infinite" }} /> Loading…
                </div>
              ) : catalogError ? (
                <div style={{ padding:"10px", fontSize:12, color:"var(--lyra-color-danger)" }}>{catalogError}</div>
              ) : (
                catalog.map(p => {
                  const isAct = !isCustom && p.id === selProv?.id;
                  return (
                    <button key={p.id} onClick={() => pickProv(p)}
                      style={{ width:"100%", display:"flex", alignItems:"center", gap:9, padding:"8px 10px", borderRadius:9, border:"none", background:isAct ? blueBg : "transparent", cursor:"pointer", textAlign:"left", marginBottom:2, transition:"background .12s" }}
                      onMouseEnter={e => { if (!isAct) e.currentTarget.style.background = "var(--lyra-color-surface-2)"; }}
                      onMouseLeave={e => { if (!isAct) e.currentTarget.style.background = "transparent"; }}
                    >
                      <div style={{ width:26, height:26, borderRadius:7, background:PROVIDER_COLORS[p.id]?.bg ?? "var(--lyra-color-surface-2)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {PROVIDER_ICONS[p.id] ?? <span style={{ fontSize:11, fontWeight:700, color:PROVIDER_COLORS[p.id]?.text ?? mu }}>{p.name[0]}</span>}
                      </div>
                      <span style={{ fontSize:12, fontWeight:isAct ? 700 : 500, color:isAct ? blue : tx, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</span>
                      {isAct && <div style={{ width:5, height:5, borderRadius:"50%", background:blue, flexShrink:0 }} />}
                    </button>
                  );
                })
              )}

              {/* Custom / Other — always at bottom */}
              {!catalogLoading && !catalogError && (
                <button onClick={pickCustom}
                  style={{ width:"100%", display:"flex", alignItems:"center", gap:9, padding:"8px 10px", borderRadius:9, border:"none", background:isCustom ? "var(--app-primary-soft)" : "transparent", cursor:"pointer", textAlign:"left", marginTop:"auto", borderTop:"1px solid "+bord, transition:"background .12s" }}
                  onMouseEnter={e => { if (!isCustom) e.currentTarget.style.background = "var(--lyra-color-surface-2)"; }}
                  onMouseLeave={e => { if (!isCustom) e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{ width:26, height:26, borderRadius:7, background:isCustom ? "var(--app-primary-soft)" : "var(--lyra-color-surface-2)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <Plus size={13} color={isCustom ? "var(--lyra-color-primary)" : mu} />
                  </div>
                  <span style={{ fontSize:12, fontWeight:isCustom ? 700 : 500, color:isCustom ? "var(--lyra-color-primary)" : su }}>Custom / Other</span>
                  {isCustom && <div style={{ width:5, height:5, borderRadius:"50%", background:"var(--lyra-color-primary)", flexShrink:0 }} />}
                </button>
              )}
            </div>

            {/* Right — model list / custom form + key */}
            <div style={{ flex:1, overflowY:"auto", padding:"16px 18px", display:"flex", flexDirection:"column", gap:14 }}>

              {isCustom ? (
                /* ── Custom provider form ── */
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:mu, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:2 }}>Custom Model Details</div>
                  <div className="add-model-form-grid-2">
                    <div>
                      <div style={{ fontSize:11, fontWeight:600, color:su, marginBottom:4 }}>Provider Name *</div>
                      <input type="text" placeholder="e.g. DeepInfra" value={customForm.providerName}
                        onChange={e => setCustomField("providerName", e.target.value)}
                        style={{ width:"100%", padding:"8px 10px", border:"1.5px solid "+bord, borderRadius:8, fontSize:12.5, color:tx, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}
                        onFocus={e => e.target.style.borderColor = blue} onBlur={e => e.target.style.borderColor = bord} />
                    </div>
                    <div>
                      <div style={{ fontSize:11, fontWeight:600, color:su, marginBottom:4 }}>Model Name *</div>
                      <input type="text" placeholder="e.g. Llama 3 70B" value={customForm.modelName}
                        onChange={e => setCustomField("modelName", e.target.value)}
                        style={{ width:"100%", padding:"8px 10px", border:"1.5px solid "+bord, borderRadius:8, fontSize:12.5, color:tx, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}
                        onFocus={e => e.target.style.borderColor = blue} onBlur={e => e.target.style.borderColor = bord} />
                    </div>
                  </div>
                  <div className="add-model-form-grid-main">
                    <div>
                      <div style={{ fontSize:11, fontWeight:600, color:su, marginBottom:4 }}>System ID * <span style={{ fontWeight:400, color:mu }}>(auto-generated)</span></div>
                      <input type="text" placeholder="custom-provider-model" value={customForm.systemId}
                        onChange={e => setCustomField("systemId", e.target.value)}
                        style={{ width:"100%", padding:"8px 10px", border:"1.5px solid "+bord, borderRadius:8, fontSize:12, color:tx, outline:"none", fontFamily:"'Fira Code','Courier New',monospace", boxSizing:"border-box" }}
                        onFocus={e => e.target.style.borderColor = blue} onBlur={e => e.target.style.borderColor = bord} />
                    </div>
                    <div>
                      <div style={{ fontSize:11, fontWeight:600, color:su, marginBottom:4 }}>Context (K)</div>
                      <input type="number" placeholder="128" value={customForm.contextK}
                        onChange={e => setCustomField("contextK", e.target.value)}
                        style={{ width:"100%", padding:"8px 10px", border:"1.5px solid "+bord, borderRadius:8, fontSize:12.5, color:tx, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}
                        onFocus={e => e.target.style.borderColor = blue} onBlur={e => e.target.style.borderColor = bord} />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:600, color:su, marginBottom:4 }}>Description <span style={{ fontWeight:400, color:mu }}>(optional)</span></div>
                    <input type="text" placeholder="Short description…" value={customForm.desc}
                      onChange={e => setCustomField("desc", e.target.value)}
                      style={{ width:"100%", padding:"8px 10px", border:"1.5px solid "+bord, borderRadius:8, fontSize:12.5, color:tx, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}
                      onFocus={e => e.target.style.borderColor = blue} onBlur={e => e.target.style.borderColor = bord} />
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:600, color:su, marginBottom:4 }}>Base URL <span style={{ fontWeight:400, color:mu }}>(optional — OpenAI-compatible endpoint)</span></div>
                    <input type="url" placeholder="https://api.deepinfra.com/v1/openai" value={customForm.baseUrl}
                      onChange={e => setCustomField("baseUrl", e.target.value)}
                      style={{ width:"100%", padding:"8px 10px", border:"1.5px solid "+bord, borderRadius:8, fontSize:12.5, color:tx, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}
                      onFocus={e => e.target.style.borderColor = blue} onBlur={e => e.target.style.borderColor = bord} />
                  </div>
                </div>
              ) : (
                /* ── Catalog model list ── */
                selProv && (
                  <div>
                    <div style={{ fontSize:10, fontWeight:700, color:mu, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Select Model</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                      {selProv.models.map(model => {
                        const isAct = model.name === selModel?.name;
                        const bs = BADGE_S[model.badge] || BADGE_S.KEY;
                        return (
                          <div key={model.name} onClick={() => pickModel(model)}
                            style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:10, border:`1.5px solid ${isAct ? "var(--app-primary-soft-2)" : bord}`, background:isAct ? blueBg : "var(--lyra-color-surface)", cursor:"pointer", transition:"all .12s" }}
                            onMouseEnter={e => { if (!isAct) { e.currentTarget.style.background = "var(--lyra-color-surface-2)"; e.currentTarget.style.borderColor = "var(--app-border-strong)"; } }}
                            onMouseLeave={e => { if (!isAct) { e.currentTarget.style.background = "var(--lyra-color-surface)"; e.currentTarget.style.borderColor = bord; } }}
                          >
                            <div style={{ width:16, height:16, borderRadius:"50%", border:isAct ? "none" : `1.5px solid ${bord}`, background:isAct ? blue : "transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                              {isAct && <Check size={9} color="var(--app-on-accent)" strokeWidth={3} />}
                            </div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                <span style={{ fontSize:13, fontWeight:600, color:tx }}>{model.name}</span>
                                {model.hot && <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4, background:"var(--lyra-color-warning-bg)", color:"#C2410C" }}>★ POPULAR</span>}
                                {model.isUserAdded && <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4, background:blueBg, color:blue }}>YOURS</span>}
                              </div>
                              <div style={{ fontSize:11.5, color:mu, marginTop:1 }}>{model.desc}</div>
                            </div>
                            <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
                              {model.contextK && <span style={{ fontSize:10, color:su, fontWeight:600 }}>{model.contextK >= 1000 ? `${model.contextK/1000}M` : `${model.contextK}K`} ctx</span>}
                              <span style={{ fontSize:10, fontWeight:700, padding:"2px 6px", borderRadius:5, background:bs.bg, color:bs.color }}>{model.badge}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              )}

              {/* API key panel */}
              <div style={{ background:"var(--lyra-color-surface-2)", borderRadius:12, padding:"14px", flexShrink:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
                  <KeyRound size={13} color={su} />
                  <span style={{ fontSize:12, fontWeight:700, color:su }}>
                    {isCustom ? "API Key" : `API Key — ${selProv?.name ?? ""}`}
                  </span>
                  {!isCustom && selProv?.keyUrl && (
                    <a href={selProv.keyUrl} target="_blank" rel="noopener noreferrer"
                      style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:4, fontSize:11, color:blue, textDecoration:"none", fontWeight:600, whiteSpace:"nowrap" }}>
                      {selProv.keyLabel} <ExternalLink size={10} />
                    </a>
                  )}
                </div>
                <div style={{ position:"relative", marginBottom:testResult ? 8 : 10 }}>
                  <input
                    type={showKey ? "text" : "password"}
                    placeholder={isCustom ? "Your API key…" : (selProv?.keyPlaceholder ?? "API key…")}
                    value={apiKey}
                    onChange={e => { setApiKey(e.target.value); setTestResult(null); }}
                    style={{ width:"100%", padding:"9px 38px 9px 12px", border:`1.5px solid ${testResult?.ok === true ? "var(--app-success-border)" : testResult?.ok === false ? "var(--app-danger-border)" : bord}`, borderRadius:9, fontSize:12.5, color:tx, outline:"none", background:"var(--lyra-color-surface)", fontFamily:"inherit", boxSizing:"border-box", transition:"border-color .15s" }}
                    onFocus={e => { e.target.style.borderColor = blue; }}
                    onBlur={e => { e.target.style.borderColor = testResult?.ok === true ? "var(--app-success-border)" : testResult?.ok === false ? "var(--app-danger-border)" : bord; }}
                  />
                  <button type="button" onClick={() => setShowKey(v => !v)} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:mu, padding:2, display:"flex" }}>
                    {showKey ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>
                {testResult && (
                  <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:600, color:testResult.ok ? "var(--lyra-color-success)" : "var(--lyra-color-danger)", marginBottom:10 }}>
                    {testResult.ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
                    {testResult.ok ? `Connected · ${testResult.latencyMs}ms latency` : (testResult.error || "Connection failed")}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, justifyContent: (testResult && testResult.ok) ? 'flex-start' : 'center' }}>
                  {(() => {
                    const canTest = apiKey.trim() && (isCustom ? (customForm.providerName.trim() && customForm.modelName.trim() && customForm.systemId.trim()) : !!selModel);
                    const testedOk = testResult && testResult.ok;
                    // Single CTA: Test first, then Save when test succeeded
                    if (!testedOk) {
                      return (
                        <button onClick={handleTest} disabled={!canTest || testing}
                          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 14px", borderRadius: 9, border: `1.5px solid ${bord}`, background: "var(--lyra-color-surface)", color: su, fontSize: 12.5, fontWeight: 600, cursor: (canTest && !testing) ? "pointer" : "not-allowed", opacity: (canTest) ? 1 : 0.45, flexShrink: 0, transition: "all .15s" }}
                          onMouseEnter={e => { if (canTest && !testing) e.currentTarget.style.background = "var(--app-surface-3)"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "var(--lyra-color-surface)"; }}
                        >
                          {testing ? <Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> : <TestTube2 size={13} />}
                          {testing ? "Testing…" : "Test Key"}
                        </button>
                      );
                    }

                    // testedOk === true -> show Save/Add primary CTA
                    const canSaveNow = testedOk && !saving && (isCustom ? (customForm.providerName.trim() && customForm.modelName.trim() && customForm.systemId.trim()) : !!selModel);
                    return (
                      <button onClick={handleSave} disabled={!canSaveNow}
                        style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px", borderRadius: 9, border: "none", background: canSaveNow ? blue : "var(--lyra-color-border)", color: canSaveNow ? "var(--lyra-color-surface)" : "var(--app-text-3)", fontSize: 12.5, fontWeight: 700, cursor: canSaveNow ? "pointer" : "not-allowed", transition: "all .15s" }}
                      >
                        {saving ? <Loader2 size={13} style={{ animation: "spin 0.7s linear infinite" }} /> : <Check size={13} />}
                        {saving ? "Saving…" : isCustom ? `Add ${customForm.modelName || "Model"}` : `Save ${selModel?.name ?? "Model"}`}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AIConsolePage() {
  // ── Model & Intelligence state ──
  const {
    models: rawModels = [], selectedMode, setSelectedMode,
    selectedModelId, setSelectedModelId, getSelectedModel,
    customApiKeys = {}, setCustomKey, toggleModel, isModelEnabled,
    refreshModels,
  } = useAI();

  // Local model list — can be drag-reordered without mutating context.
  // Resets when the server adds or removes models.
  const [models, setModels] = useState(rawModels);
  useEffect(() => { setModels(rawModels); }, [rawModels]); // eslint-disable-line

  const [saveFlash, setSaveFlash] = useState(false);

  // Draft state for the "active model" manual-mode key input below — same
  // save-on-blur-with-verification treatment as ModelRow's key input
  // (setCustomKey now live-verifies server-side and can reject).
  const [activeKeyId, setActiveKeyId] = useState(null);
  const [activeKeyDraft, setActiveKeyDraft] = useState("");
  const [savingActiveKey, setSavingActiveKey] = useState(false);
  async function handleActiveKeyBlur(modelId, currentSaved) {
    if (activeKeyDraft === currentSaved) return;
    setSavingActiveKey(true);
    try {
      await setCustomKey(modelId, activeKeyDraft);
    } catch (err) {
      const detail = err?.response?.data?.details?.[modelId] || err?.response?.data?.error || err.message;
      toast.error(detail || "Failed to save key");
      setActiveKeyDraft(currentSaved);
    } finally {
      setSavingActiveKey(false);
    }
  }

  function handleToggle(modelId) {
    toggleModel(modelId);
    setSaveFlash(true);
    setTimeout(() => setSaveFlash(false), 2000);
  }

  const enabledCount = models.filter(m => isModelEnabled(m.id)).length;
  const activeModel  = getSelectedModel ? getSelectedModel() : null;

  async function handleDeleteUserModel(e, model) {
    e.stopPropagation();
    if (!model || !model.isUserAdded) return;
    if (!window.confirm(`Delete your saved model "${model.name}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/models/user-models/${model.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      toast.success('Model removed');
      // clear selection if it was the deleted model
      if (selectedModelId === model.id) setSelectedModelId(null);
      await refreshModels();
    } catch (err) {
      console.error('Delete model error', err);
      toast.error(err.message || 'Failed to delete model');
    }
  }

  // ── State for the new Model & Intelligence UI ─────────────────────────────
  const [showKey,      setShowKey]      = useState(false);
  const [testKeyState, setTestKeyState] = useState(null); // null | 'loading' | 'success' | 'error'
  const [dragIndex,    setDragIndex]    = useState(null);
  const [dropIndex,    setDropIndex]    = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pageTab,      setPageTab]      = useState("models"); // models | keys
  const [keysFocus,    setKeysFocus]    = useState(null);

  async function handleTestKey(modelId) {
    const key = customApiKeys[modelId];
    if (!key) return;
    setTestKeyState('loading');
    try {
      const res = await fetch(`${API_BASE}/api/usage/test-key`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ modelId, apiKey: key }),
      });
      const d = await res.json();
      setTestKeyState(d.ok ? 'success' : 'error');
      setTimeout(() => setTestKeyState(null), 3000);
    } catch {
      setTestKeyState('error');
      setTimeout(() => setTestKeyState(null), 3000);
    }
  }

  // Cost & Budget moved to header popover; state handled there

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }
        input[type=range] { cursor: pointer; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: var(--lyra-color-border); border-radius: 4px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .ai-console-header { display:flex; align-items:center; gap:12px; }
        .ai-console-header-actions { margin-left:auto; display:flex; gap:8px; align-items:center; }
        .ai-console-body { max-width:1600px; margin:0 auto; padding:24px 32px 40px; }
        .ai-console-main-grid { display:grid; grid-template-columns:2fr 1fr; gap:20px; align-items:start; margin-bottom:24px; }
        .ai-console-mode-row { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; gap:10px; }
        .ai-console-active-strip { display:flex; align-items:center; gap:10px; }
        .ai-console-active-controls { display:flex; align-items:center; gap:6px; }
        .ai-console-active-key-wrap { position:relative; width:160px; }
        .ai-console-active-key-input { width:100%; }
        .ai-console-model-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(150px, 1fr)); gap:8px; margin-bottom:14px; }
        .ai-console-engram-row { display:flex; align-items:center; gap:14px; }
        .ai-console-engram-title-row { display:flex; align-items:center; gap:6px; }
        .ai-console-engram-actions { flex-shrink:0; display:flex; flex-direction:column; gap:5px; align-items:stretch; }
        .ai-console-engram-key-row { display:flex; gap:8px; }
        .ai-console-memory-search-row { display:flex; gap:8px; }
        .add-model-shell { background:var(--lyra-color-surface); border-radius:18px; box-shadow:0 28px 80px rgba(0,0,0,0.22), 0 4px 20px rgba(0,0,0,0.08); width:100%; max-width:730px; max-height:90vh; display:flex; flex-direction:column; animation:amIn 0.24s cubic-bezier(0.34,1.3,0.64,1); overflow:hidden; }
        .add-model-content { display:flex; flex:1; overflow:hidden; min-height:0; }
        .add-model-provider-col { width:190px; border-right:1px solid var(--lyra-color-border); overflow-y:auto; padding:10px 8px; flex-shrink:0; background:var(--lyra-color-surface-2); display:flex; flex-direction:column; }
        .add-model-form-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .add-model-form-grid-main { display:grid; grid-template-columns:1fr 90px; gap:8px; }

        ${modelsTabCss}

        @media (max-width: 1024px) {
          .ai-console-main-grid { grid-template-columns:1fr; gap:14px; }
        }

        @media (max-width: 768px) {
          .ai-console-header { flex-wrap:wrap; align-items:flex-start; }
          .ai-console-header-actions { margin-left:0; width:100%; flex-wrap:wrap; justify-content:flex-start; }
          .ai-console-body { padding:14px 12px 18px; }
          .ai-console-main-grid { grid-template-columns:1fr; gap:12px; margin-bottom:14px; }
          .ai-console-mode-row { flex-direction:column; align-items:flex-start; }
          .ai-console-active-strip { flex-direction:column; align-items:flex-start; }
          .ai-console-active-controls { width:100%; flex-wrap:wrap; }
          .ai-console-active-key-wrap { width:100%; }
          .ai-console-model-grid { grid-template-columns:repeat(auto-fill, minmax(130px, 1fr)); }
          .ai-console-engram-row { flex-direction:column; align-items:flex-start; }
          .ai-console-engram-title-row { flex-wrap:wrap; }
          .ai-console-engram-actions { width:100%; }
          .ai-console-engram-key-row { flex-direction:column; }
          .ai-console-memory-search-row { flex-direction:column; }
          .add-model-shell { max-height:95vh; border-radius:14px; }
          .add-model-content { flex-direction:column; overflow:auto; }
          .add-model-provider-col { width:100%; border-right:none; border-bottom:1px solid var(--lyra-color-border); padding:8px; max-height:220px; }
          .add-model-form-grid-2 { grid-template-columns:1fr; }
          .add-model-form-grid-main { grid-template-columns:1fr; }
        }
      `}</style>

      <div style={{ minHeight: "100vh", background: "var(--lyra-color-surface)", overflowX: "hidden" }}>

        {/* ── Header ── */}
        <div style={{ background: "var(--lyra-color-surface)", padding: "20px 32px 0", borderBottom: "1px solid " + bord }}>
          <div className="ai-console-header" style={{ alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, color: tx, letterSpacing: "-0.01em" }}>Models</div>
              <div style={{ fontSize: 13, color: su, marginTop: 4 }}>Choose which models Lyra can use and which one is the default.</div>
            </div>
            <div className="ai-console-header-actions">
              <button
                onClick={() => setShowAddModal(true)}
                style={{ height: 36, padding: "0 16px", borderRadius: 10, border: "none", background: blue, color: "var(--app-on-accent)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Add custom model
              </button>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            {[["models", "Models"], ["keys", "API keys"]].map(([id, label]) => (
              <button key={id} onClick={() => { setPageTab(id); if (id !== "keys") setKeysFocus(null); }}
                style={{ padding: "9px 14px", border: "none", background: "none", fontSize: 13.5, fontWeight: 600, color: pageTab === id ? blue : tx, cursor: "pointer", borderBottom: "2.5px solid " + (pageTab === id ? blue : "transparent"), marginBottom: -1 }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="ai-console-body">

          {pageTab === "models" && (
            <ModelsTab
              rawModels={rawModels}
              customApiKeys={customApiKeys}
              selectedMode={selectedMode}
              setSelectedMode={setSelectedMode}
              selectedModelId={selectedModelId}
              setSelectedModelId={setSelectedModelId}
              refreshModels={refreshModels}
              onOpenKeys={(pid) => { setKeysFocus(pid); setPageTab("keys"); }}
            />
          )}

          {pageTab === "keys" && (
            <ApiKeysTab customApiKeys={customApiKeys} setCustomKey={setCustomKey} focusProvider={keysFocus} />
          )}


        </div>
      </div>

      {/* ── Add Model Modal ── */}
      {showAddModal && (
        <AddModelModal
          existingKeys={customApiKeys}
          onClose={() => setShowAddModal(false)}
          onSave={async (modelId, key) => { setCustomKey(modelId, key); }}
          onUserModelSaved={async () => { await refreshModels(); }}
        />
      )}

    </>
  );
}
